import { Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { clearTempHours } from "./api/AdminManager/Payroll/Payslip/PayslipApi";

import Layout from "./components/Layout";
import AdminLayout from "./components/AdminLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import BiometricsRoute from "./components/BiometricsRoute";
import PageLoading from "./components/PageLoading";
import SessionExpiryManager from "./components/SessionExpiryManager";
import { SidebarProvider } from "./context/SidebarContext";
import { AuthProvider } from "./context/AuthContext";
import { PayCycleProvider } from "./context/PayCycleContext";
import { EmployeeAddProvider } from "./context/EmployeeAddContext";
import { EmployeeSessionProvider } from "./context/EmployeeSessionContext";
import Landing from "./pages/auth/views/Landing";
import { lazyWithReload } from "./utils/lazyWithReload";

// Every other page is split into its own chunk and loaded the first time its route opens,
// so the initial download is just the login screen and app shell.
const Register = lazyWithReload(() => import("./pages/auth/views/Register"));
const Dashboard = lazyWithReload(() => import("./pages/AdminManager/views/dashboard"));
const BioMetricsDashboard = lazyWithReload(() => import("./pages/Biometrics/views/BioMetricsDashboard"));
const History = lazyWithReload(() => import("./pages/Biometrics/views/History"));
const AttendanceCalendar = lazyWithReload(() => import("./pages/Biometrics/views/AttendanceCalendar"));
const CompanyManagement = lazyWithReload(() => import("./pages/AdminManager/CompanyManagement/views/CompanyManagement"));
const PayCycle = lazyWithReload(() => import("./pages/AdminManager/PayCycle/views/PayCycle"));
const AddCycle = lazyWithReload(() => import("./pages/AdminManager/PayCycle/views/AddCycle"));
const EmployeeManagement = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/EmployeeManagement"));
const AddEmployee = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/AddEmployee"));
const AddressDetails = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/AddressDetails"));
const ContactDetails = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/ContactDetails"));
const EmploymentDetails = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/EmploymentDetails"));
const Rates = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/Rates"));
const EmployeeViewLayout = lazyWithReload(() => import("./pages/AdminManager/EmployeeManagement/views/EmployeeViewLayout"));
const PayrollLanding = lazyWithReload(() => import("./pages/AdminManager/Payroll/PayrollLanding"));
const HoursAndRates = lazyWithReload(() => import("./pages/AdminManager/Payroll/HoursAndRates/views/HoursAndRates"));
const EmploymentTaxIncentive = lazyWithReload(() => import("./pages/AdminManager/Payroll/EmploymentTaxIncentive/views/EmploymentTaxIncentive"));
const Payslip = lazyWithReload(() => import("./pages/AdminManager/Payroll/payslip/views/Payslip"));
const GeneratedPayslip = lazyWithReload(() => import("./pages/AdminManager/Payroll/payslip/views/GeneratedPayslip"));
const HistoricalPayslip = lazyWithReload(() => import("./pages/AdminManager/Payroll/payslip/views/HistoricalPayslip"));
const AnnualValues = lazyWithReload(() => import("./pages/AdminManager/Payroll/annual-values/views/AnnualValues"));
const LeaveApplication = lazyWithReload(() => import("./pages/AdminManager/Payroll/leave-application/views/LeaveApplication"));
const Transactions = lazyWithReload(() => import("./pages/AdminManager/Payroll/transactions/views/Transactions"));
const LeaveHistory = lazyWithReload(() => import("./pages/AdminManager/Payroll/LeaveHistory/views/LeaveHistory"));
const ReportLanding = lazyWithReload(() => import("./pages/AdminManager/Report/reportLanding"));
const Financial = lazyWithReload(() => import("./pages/AdminManager/Report/Financial/views/Financial"));
const Leave = lazyWithReload(() => import("./pages/AdminManager/Report/Leave/views/Leave"));
const TaxReconciliation = lazyWithReload(() => import("./pages/AdminManager/Report/TaxReconciliation/views/TaxReconciliation"));
const AdminCompanies = lazyWithReload(() => import("./pages/Admin/views/AdminCompanies"));
const AdminCompanyDetails = lazyWithReload(() => import("./pages/Admin/views/AdminCompanyDetails"));
const DailyUsageView = lazyWithReload(() => import("./pages/Kitchen/daily-usage/DailyUsageView"));
const MenuItemsView = lazyWithReload(() => import("./pages/Kitchen/menu-items/MenuItemsView"));
const MonthlyIntakeView = lazyWithReload(() => import("./pages/Kitchen/monthly-intake/MonthlyIntakeView"));
const ReservationsView = lazyWithReload(() => import("./pages/Kitchen/reservations/ReservationsView"));
const KitchenAnalyticsView = lazyWithReload(() => import("./pages/Kitchen/analytics/KitchenAnalyticsView"));

// Temp last-day hours only live while viewing one employee's payroll pages (/payroll/...).
function TempHoursReset() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (!pathname.startsWith("/payroll/")) clearTempHours();
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
      <SessionExpiryManager />
      <TempHoursReset />
      <Suspense fallback={<PageLoading message="Loading" />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/register" element={<Register />} />
        <Route element={<ProtectedRoute />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminCompanies />} />
            <Route path="/admin/:id" element={<AdminCompanyDetails />} />
          </Route>
          <Route element={<SidebarProvider><Layout /></SidebarProvider>}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route element={<BiometricsRoute />}>
              <Route path="/biometrics" element={<BioMetricsDashboard />} />
              <Route path="/history" element={<History />} />
              <Route path="/attendance-calendar" element={<AttendanceCalendar />} />
            </Route>
            <Route path="/company-management" element={<CompanyManagement />} />
            <Route path="/pay-cycle" element={<PayCycle />} />
            <Route path="/pay-cycle/add" element={
              <PayCycleProvider>
                <AddCycle />
              </PayCycleProvider>
            } />
            <Route path="/payroll" element={<PayrollLanding />} />
            <Route path="/employee-management" element={<EmployeeManagement />} />
            <Route path="/employee-management/add" element={
              <EmployeeSessionProvider>
                <EmployeeAddProvider>
                  <AddEmployee />
                </EmployeeAddProvider>
              </EmployeeSessionProvider>
            } />
            <Route path="/employee-management/add/address" element={
              <EmployeeSessionProvider>
                <EmployeeAddProvider>
                  <AddressDetails />
                </EmployeeAddProvider>
              </EmployeeSessionProvider>
            } />
            <Route path="/employee-management/add/address/contact" element={
              <EmployeeSessionProvider>
                <EmployeeAddProvider>
                  <ContactDetails />
                </EmployeeAddProvider>
              </EmployeeSessionProvider>
            } />
            <Route path="/employee-management/add/address/contact/employment" element={
              <EmployeeSessionProvider>
                <EmployeeAddProvider>
                  <EmploymentDetails />
                </EmployeeAddProvider>
              </EmployeeSessionProvider>
            } />
            <Route path="/employee-management/add/address/contact/employment/rates" element={
              <EmployeeSessionProvider>
                <EmployeeAddProvider>
                  <Rates />
                </EmployeeAddProvider>
              </EmployeeSessionProvider>
            } />
            <Route path="/employee-management/view" element={
              <EmployeeSessionProvider>
                <EmployeeViewLayout />
              </EmployeeSessionProvider>
            }>
              <Route index element={<AddEmployee />} />
              <Route path="address" element={<AddressDetails />} />
              <Route path="address/contact" element={<ContactDetails />} />
              <Route path="address/contact/employment" element={<EmploymentDetails />} />
              <Route path="address/contact/employment/rates" element={<Rates />} />
            </Route>
            <Route path="/payroll/hours-and-rates" element={<HoursAndRates />} />
            <Route path="/payroll/employment-tax-incentive" element={<EmploymentTaxIncentive />} />
            <Route path="/payroll/payslip" element={<Payslip />} />
            <Route path="/payroll/annual-values" element={<AnnualValues />} />
            <Route path="/payroll/leave-applications" element={<LeaveApplication />} />
            <Route path="/payroll/payslip/generated" element={<GeneratedPayslip />} />
            <Route path="/payroll/payslip/historical" element={<HistoricalPayslip />} />
            <Route path="/payroll/transactions" element={<Transactions />} />
            <Route path="/payroll/leave-history" element={<LeaveHistory />} />
            <Route path="/report" element={<ReportLanding />} />
            <Route path="/report/financial" element={<Financial />} />
            <Route path="/report/leave" element={<Leave />} />
            <Route path="/report/tax-reconciliation" element={<TaxReconciliation />} />
            <Route path="/kitchen" element={<Navigate to="/kitchen/daily-usage" replace />} />
            <Route path="/kitchen/daily-usage" element={<DailyUsageView />} />
            <Route path="/kitchen/reservations" element={<ReservationsView />} />
            <Route path="/kitchen/intake" element={<MonthlyIntakeView />} />
            <Route path="/kitchen/menu" element={<MenuItemsView />} />
            <Route path="/kitchen/analytics" element={<KitchenAnalyticsView />} />
          </Route>
        </Route>
        </Routes>
      </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}
